/**
 * Three.js scene for the voxel world. Rendering only: reads the world, never
 * mutates it.
 *
 * Deliberate deviation from the template interface: `previous` is a lightweight
 * `CameraSnapshot`, not a `World`. The template cloned its whole world per tick,
 * but a voxel world carries a chunk store that can hold megabytes — cloning it
 * at 20 Hz is not an option. The render side only ever needed previous-frame
 * kinematics for interpolation, so that is exactly what the snapshot carries.
 *
 * Chunk meshes are owned here, keyed by chunk coordinate: each render ensures
 * the window of chunks within radius 4 horizontally and +/-2 vertically around
 * the camera (clamped to the world's vertical bounds) is meshed, budgeted to a
 * few chunks per frame so a 400-chunk sweep never stalls a single frame, and
 * disposes everything that falls out of range. Meshes are keyed by coordinate
 * only, so a new run (new seed) purges the whole map. In-run block edits do
 * not exist in M0; M1 mining must additionally invalidate the edited chunk.
 *
 * Everything disposable — chunk geometries, shared materials, the atlas, the
 * WebGL context itself — is released in `dispose()`. Restarting a scene five
 * times must not grow the heap.
 */

import * as THREE from "three";
import { WORLD_CEILING, WORLD_FLOOR } from "../data/worldgen";
import { CHUNK_SIZE, chunkCoord, chunkKey } from "../sim/chunk";
import type { World } from "../sim/world";
import { ATLAS_TILE_COUNT, createAtlasTexture } from "./atlas";
import { buildChunkGeometry } from "./mesher";

/** Previous-tick kinematics for render interpolation. See the file header. */
export interface CameraSnapshot {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
}

export interface Renderer {
  render(world: World, previous: CameraSnapshot | null, alpha: number): void;
  resize(): void;
  dispose(): void;
}

/** Eye height above the feet position the sim tracks. */
const EYE_HEIGHT = 1.62;
/** Chunk window around the camera: 9x9 columns, 5 chunks tall. */
const VIEW_RADIUS = 4;
const VIEW_RADIUS_Y = 2;
/** New chunks meshed per render call; the closest are done first. */
const MESH_BUDGET_PER_FRAME = 6;
/** Light blue meadow sky; fog matches so distance fades into the sky. */
const SKY = 0x87bfe8;

const MIN_CY = Math.floor(WORLD_FLOOR / CHUNK_SIZE);
const MAX_CY = Math.floor(WORLD_CEILING / CHUNK_SIZE);

interface ChunkMeshes {
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;
  opaque?: THREE.Mesh;
  transparent?: THREE.Mesh;
}

export function createRenderer(canvas: HTMLCanvasElement): Renderer {
  const three = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    powerPreference: "high-performance",
    // The e2e paint check reads pixels back after the frame; without this the
    // drawing buffer is cleared as soon as the compositor takes it.
    preserveDrawingBuffer: true,
  });

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(SKY);
  scene.fog = new THREE.Fog(SKY, 48, VIEW_RADIUS * CHUNK_SIZE - 8);

  const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
  // YXZ: yaw about the world Y axis first, then pitch — matches the sim's
  // look direction exactly (yaw 0 faces -z).
  camera.rotation.order = "YXZ";

  const hemisphere = new THREE.HemisphereLight(0xd8ecff, 0x8a7a5c, 0.85);
  const sun = new THREE.DirectionalLight(0xfff2dc, 1.2);
  sun.position.set(0.6, 1, 0.35);
  scene.add(hemisphere, sun);

  const atlas = createAtlasTexture();
  const opaqueMaterial = new THREE.MeshLambertMaterial({ map: atlas.texture, vertexColors: true });
  const waterMaterial = new THREE.MeshLambertMaterial({
    map: atlas.texture,
    vertexColors: true,
    transparent: true,
    opacity: 0.75,
    depthWrite: false,
  });

  const chunkMeshes = new Map<string, ChunkMeshes>();
  let meshSeed: number | null = null;
  let lastCamChunk = "";
  let meshBacklog = true;

  const disposers: Array<() => void> = [];

  const resize = (): void => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, canvas.clientWidth);
    const height = Math.max(1, canvas.clientHeight);
    three.setPixelRatio(dpr);
    three.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  };

  const onResize = (): void => resize();
  window.addEventListener("resize", onResize, { passive: true });
  disposers.push(() => window.removeEventListener("resize", onResize));

  // A lost context is normal on mobile after backgrounding. Preventing the
  // default lets the browser restore it instead of leaving a black rectangle.
  const onContextLost = (event: Event): void => event.preventDefault();
  canvas.addEventListener("webglcontextlost", onContextLost);
  canvas.addEventListener("contextlost", onContextLost);
  disposers.push(() => {
    canvas.removeEventListener("webglcontextlost", onContextLost);
    canvas.removeEventListener("contextlost", onContextLost);
  });

  const dropChunk = (key: string, meshes: ChunkMeshes): void => {
    if (meshes.opaque) {
      scene.remove(meshes.opaque);
      meshes.opaque.geometry.dispose();
    }
    if (meshes.transparent) {
      scene.remove(meshes.transparent);
      meshes.transparent.geometry.dispose();
    }
    chunkMeshes.delete(key);
  };

  const resetChunks = (): void => {
    for (const [key, meshes] of chunkMeshes) dropChunk(key, meshes);
    meshBacklog = true;
    lastCamChunk = "";
  };

  const addChunk = (world: World, cx: number, cy: number, cz: number, key: string): void => {
    const { opaque, transparent } = buildChunkGeometry(world, cx, cy, cz, ATLAS_TILE_COUNT);
    const meshes: ChunkMeshes = { cx, cy, cz };
    if (opaque) {
      const mesh = new THREE.Mesh(opaque, opaqueMaterial);
      mesh.position.set(cx * CHUNK_SIZE, cy * CHUNK_SIZE, cz * CHUNK_SIZE);
      // Chunk meshes never move; skip the per-frame matrix recompute.
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      scene.add(mesh);
      meshes.opaque = mesh;
    }
    if (transparent) {
      const mesh = new THREE.Mesh(transparent, waterMaterial);
      mesh.position.set(cx * CHUNK_SIZE, cy * CHUNK_SIZE, cz * CHUNK_SIZE);
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      scene.add(mesh);
      meshes.transparent = mesh;
    }
    chunkMeshes.set(key, meshes);
  };

  const ensureChunks = (world: World, px: number, py: number, pz: number): void => {
    const ccx = chunkCoord(Math.floor(px));
    const ccy = chunkCoord(Math.floor(py));
    const ccz = chunkCoord(Math.floor(pz));
    const camKey = chunkKey(ccx, ccy, ccz);
    // The sweep is cheap but pointless once the window is fully meshed and
    // the camera has not crossed a chunk boundary.
    if (!meshBacklog && camKey === lastCamChunk) return;
    lastCamChunk = camKey;

    for (const [key, meshes] of chunkMeshes) {
      if (
        Math.abs(meshes.cx - ccx) > VIEW_RADIUS ||
        Math.abs(meshes.cz - ccz) > VIEW_RADIUS ||
        Math.abs(meshes.cy - ccy) > VIEW_RADIUS_Y ||
        meshes.cy < MIN_CY ||
        meshes.cy > MAX_CY
      ) {
        dropChunk(key, meshes);
      }
    }

    const missing: Array<{ key: string; cx: number; cy: number; cz: number; d: number }> = [];
    for (let dy = -VIEW_RADIUS_Y; dy <= VIEW_RADIUS_Y; dy++) {
      const cy = ccy + dy;
      if (cy < MIN_CY || cy > MAX_CY) continue;
      for (let dx = -VIEW_RADIUS; dx <= VIEW_RADIUS; dx++) {
        for (let dz = -VIEW_RADIUS; dz <= VIEW_RADIUS; dz++) {
          const cx = ccx + dx;
          const cz = ccz + dz;
          const key = chunkKey(cx, cy, cz);
          if (!chunkMeshes.has(key)) {
            missing.push({ key, cx, cy, cz, d: dx * dx + dy * dy + dz * dz });
          }
        }
      }
    }
    missing.sort((a, b) => a.d - b.d);
    for (const chunk of missing.slice(0, MESH_BUDGET_PER_FRAME)) {
      addChunk(world, chunk.cx, chunk.cy, chunk.cz, chunk.key);
    }
    meshBacklog = missing.length > MESH_BUDGET_PER_FRAME;
  };

  resize();

  return {
    resize,

    render(world, previous, alpha) {
      if (meshSeed !== world.seed) {
        // New run, new terrain: every cached mesh belongs to the old world.
        resetChunks();
        meshSeed = world.seed;
      }

      // Interpolate between the last two sim states so motion is smooth
      // without coupling the render rate to the simulation rate.
      const px = previous ? lerp(previous.x, world.position.x, alpha) : world.position.x;
      const py = previous ? lerp(previous.y, world.position.y, alpha) : world.position.y;
      const pz = previous ? lerp(previous.z, world.position.z, alpha) : world.position.z;
      camera.position.set(px, py + EYE_HEIGHT, pz);
      camera.rotation.set(world.pitch, world.yaw, 0);

      ensureChunks(world, px, py, pz);
      three.render(scene, camera);
    },

    dispose() {
      resetChunks();
      opaqueMaterial.dispose();
      waterMaterial.dispose();
      atlas.dispose();
      while (disposers.length) disposers.pop()?.();
      three.dispose();
      three.forceContextLoss();
    },
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
